import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  LogOut, Users, FileText, UserPlus, Plus, Search, 
  Mail, Phone, MapPin, Calendar, DollarSign,
  Menu, X, LayoutDashboard, ChevronLeft, ChevronRight,
  TrendingUp, Activity, Briefcase, ArrowRight, Eye, Loader2, Trash2, Settings
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import QuoteView from '../components/QuoteView';
import axios from 'axios';
import { API_URL } from '../config/api';
import { APP_LOGO_SRC } from '../config/branding';


const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isAuthenticated, loading: authLoading } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [contacts, setContacts] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [quoteSearchTerm, setQuoteSearchTerm] = useState('');
  const [quoteCurrentPage, setQuoteCurrentPage] = useState(1);
  const [contactCurrentPage, setContactCurrentPage] = useState(1);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [quoteViewOpen, setQuoteViewOpen] = useState(false);
  const [teamMemberDialogOpen, setTeamMemberDialogOpen] = useState(false);
  const [newTeamMember, setNewTeamMember] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    role: 'user',
    phone: ''
  });
  const [creatingMember, setCreatingMember] = useState(false);
  const [syncingGHL, setSyncingGHL] = useState(false);
  const [ghlSyncMessage, setGhlSyncMessage] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);
  const [contactDetailsOpen, setContactDetailsOpen] = useState(false);
  const [deleteMemberDialogOpen, setDeleteMemberDialogOpen] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState(null);
  const [deletingMember, setDeletingMember] = useState(false);
  const [pricingSettings, setPricingSettings] = useState({
    discount_enabled: false,
    discount_percentage: 0,
    margin_enabled: false,
    margin_percentage: 35,
    surcharge_enabled: false,
    surcharge_amount: 0,
    workers_fixing_enabled: false,
    workers_fixing_amount: 1000,
    material_cost_enabled: false,
    material_cost_amount: 150
  });
  const [savingPricingSettings, setSavingPricingSettings] = useState(false);
  
  const quotesPerPage = 5;

  // Helper function to get initials
  const getInitials = (firstName, lastName) => {
    const first = firstName ? firstName.charAt(0).toUpperCase() : '';
    const last = lastName ? lastName.charAt(0).toUpperCase() : '';
    return first + last || '?';
  };

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/');
      return;
    }
    fetchData();
  }, [isAuthenticated, authLoading, navigate]);

  // Check for tab parameter in URL whenever location changes
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const tab = urlParams.get('tab');
    if (tab && ['overview', 'contacts', 'team', 'quotes', 'pricing-settings'].includes(tab)) {
      setActiveTab(tab);
    }
  }, [location.search]);

  const fetchData = async () => {
    const headers = { Authorization: `Bearer ${localStorage.getItem('access_token')}` };
    const requestConfig = { headers, timeout: 30000 };
    let pending = 4;
    const markDone = () => {
      pending -= 1;
      if (pending <= 0) setLoading(false);
    };

    setLoading(true);

    axios
      .get(`${API_URL}/api/contacts`, requestConfig)
      .then((response) => setContacts(response.data))
      .catch((error) => console.error('Error fetching contacts:', error))
      .finally(markDone);

    axios
      .get(`${API_URL}/api/team`, requestConfig)
      .then((response) => setTeamMembers(response.data))
      .catch((error) => console.error('Error fetching team:', error))
      .finally(markDone);

    axios
      .get(`${API_URL}/api/quotes`, requestConfig)
      .then((response) => setQuotes(response.data))
      .catch((error) => console.error('Error fetching quotes:', error))
      .finally(markDone);

    axios
      .get(`${API_URL}/api/pricing-settings`, requestConfig)
      .then((response) => {
        if (response?.data) setPricingSettings(response.data);
      })
      .catch(() => {})
      .finally(markDone);
  };

  const handleSavePricingSettings = async () => {
    try {
      setSavingPricingSettings(true);
      
      // Validate and correct margin minimum; discount is set per quote, not in admin
      let settingsToSave = { ...pricingSettings };
      settingsToSave.discount_enabled = false;
      settingsToSave.discount_percentage = 0;
      if (settingsToSave.margin_enabled) {
        const marginValue = parseFloat(settingsToSave.margin_percentage);
        if (isNaN(marginValue) || marginValue < 35) {
          settingsToSave.margin_percentage = 35;
          setPricingSettings(settingsToSave);
        }
      }
      
      const response = await axios.put(
        `${API_URL}/api/pricing-settings`,
        settingsToSave,
        {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
        }
      );
      let finalSettings = response.data;
      if (user?.role === 'admin' || user?.role === 'Admin') {
        try {
          const adminResponse = await axios.put(
            `${API_URL}/api/admin/pricing-settings`,
            {
              workers_fixing_enabled: Boolean(pricingSettings.workers_fixing_enabled),
              workers_fixing_amount: Number(pricingSettings.workers_fixing_amount) || 1000,
              material_cost_enabled: Boolean(pricingSettings.material_cost_enabled),
              material_cost_amount: Number(pricingSettings.material_cost_amount) || 150
            },
            { headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` } }
          );
          finalSettings = adminResponse.data;
        } catch (adminErr) {
          console.error('Error saving admin pricing settings:', adminErr);
        }
      }
      setPricingSettings(finalSettings);
      alert('Pricing settings saved successfully!');
    } catch (error) {
      console.error('Error saving pricing settings:', error);
      const errorMessage = error.response?.data?.detail || 'Error saving pricing settings.';
      alert(errorMessage);
    } finally {
      setSavingPricingSettings(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleDeleteMember = async () => {
    if (!memberToDelete) return;
    
    try {
      setDeletingMember(true);
      await axios.delete(`${API_URL}/api/team/${memberToDelete.id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      
      // Refresh team members list
      const teamRes = await axios.get(`${API_URL}/api/team`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      setTeamMembers(teamRes.data);
      
      setDeleteMemberDialogOpen(false);
      setMemberToDelete(null);
    } catch (error) {
      console.error('Error deleting team member:', error);
      alert(error.response?.data?.detail || 'Failed to delete team member');
    } finally {
      setDeletingMember(false);
    }
  };

  const openDeleteDialog = (member) => {
    setMemberToDelete(member);
    setDeleteMemberDialogOpen(true);
  };

  const filteredContacts = contacts.filter(contact => {
    const search = searchTerm.toLowerCase();
    return (
      contact.first_name?.toLowerCase().includes(search) ||
      contact.last_name?.toLowerCase().includes(search) ||
      contact.email?.toLowerCase().includes(search) ||
      contact.phone?.toLowerCase().includes(search)
    );
  });

  // Reset to page 1 when search term changes
  useEffect(() => {
    setContactCurrentPage(1);
  }, [searchTerm]);

  // Calculate pagination for contacts
  const contactsPerPage = 50;
  const totalContactPages = Math.ceil(filteredContacts.length / contactsPerPage);
  const contactStartIndex = (contactCurrentPage - 1) * contactsPerPage;
  const contactEndIndex = contactStartIndex + contactsPerPage;
  const paginatedContacts = filteredContacts.length > contactsPerPage 
    ? filteredContacts.slice(contactStartIndex, contactEndIndex)
    : filteredContacts;

  const filteredQuotes = quotes.filter(quote => {
    if (!quoteSearchTerm) return true;
    const search = quoteSearchTerm.toLowerCase();
    const clientDetails = quote.quote_data?.clientDetails || {};
    const clientName = clientDetails.name || '';
    const clientEmail = clientDetails.email || '';
    const quoteNumber = quote.quote_number || '';
    
    return (
      clientName.toLowerCase().includes(search) ||
      clientEmail.toLowerCase().includes(search) ||
      quoteNumber.toLowerCase().includes(search)
    );
  });

  // Reset to page 1 when search term changes
  useEffect(() => {
    setQuoteCurrentPage(1);
  }, [quoteSearchTerm]);

  // Calculate pagination
  const totalQuotePages = Math.ceil(filteredQuotes.length / quotesPerPage);
  const startIndex = (quoteCurrentPage - 1) * quotesPerPage;
  const endIndex = startIndex + quotesPerPage;
  const paginatedQuotes = filteredQuotes.length > quotesPerPage 
    ? filteredQuotes.slice(startIndex, endIndex)
    : filteredQuotes;

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-800 border-gray-200',
      sent: 'bg-blue-50 text-blue-700 border-blue-200',
      accepted: 'bg-green-50 text-green-700 border-green-200',
      rejected: 'bg-red-50 text-red-700 border-red-200',
      expired: 'bg-yellow-50 text-yellow-700 border-yellow-200',
    };
    return colors[status] || colors.draft;
  };

  const menuItems = [
    { id: 'overview', label: 'Overview', icon: LayoutDashboard },
    { id: 'contacts', label: 'Contacts', icon: Users },
    { id: 'team', label: 'Team', icon: UserPlus },
    { id: 'quotes', label: 'Quotes', icon: FileText },
    { id: 'pricing-settings', label: 'Pricing Settings', icon: Settings },
  ];

  if (!isAuthenticated || !user) {
    if (authLoading) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center text-gray-600">
          Loading…
        </div>
      );
    }
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <aside className={`${sidebarOpen ? 'w-64' : 'w-20'} bg-white border-r border-gray-200 transition-all duration-300 fixed h-full z-40`}>
        <div className="flex flex-col h-full">
          {/* Logo/Header */}
          <div className="h-16 border-b border-gray-200 flex items-center justify-between px-4">
            {sidebarOpen ? (
              <div className="flex items-center space-x-2">
                <img
                  src={APP_LOGO_SRC}
                  alt="config-go"
                  className="h-10 w-auto object-contain"
                />
              </div>
            ) : (
              <div className="mx-auto h-8 w-10 overflow-hidden">
                <img
                  src={APP_LOGO_SRC}
                  alt="config-go"
                  className="h-8 w-auto max-w-none object-left object-contain"
                />
              </div>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="h-8 w-8"
            >
              {sidebarOpen ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </Button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto py-4">
            <div className="px-3 space-y-1">
              {menuItems.map((item) => {
                const Icon = item.icon;
                const isActive = activeTab === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => setActiveTab(item.id)}
                    className={`w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg transition-all ${
                      isActive
                        ? 'bg-orange-50 text-orange-700 border border-orange-200'
                        : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    <Icon className={`h-5 w-5 ${isActive ? 'text-orange-600' : 'text-gray-500'}`} />
                    {sidebarOpen && <span className="font-medium">{item.label}</span>}
                  </button>
                );
              })}
            </div>
          </nav>

          {/* User Section */}
          <div className="border-t border-gray-200 p-4">
            <div className={`flex items-center ${sidebarOpen ? 'justify-between' : 'justify-center'}`}>
              {sidebarOpen ? (
                <>
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                      <span className="text-orange-600 font-semibold text-sm">
                        {user.first_name[0]}{user.last_name[0]}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {user.first_name} {user.last_name}
                      </p>
                      <p className="text-xs text-gray-500 truncate">{user.email}</p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleLogout}
                    className="h-8 w-8 text-gray-500 hover:text-red-600"
                  >
                    <LogOut className="h-4 w-4" />
                  </Button>
                </>
              ) : (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleLogout}
                  className="h-8 w-8 text-gray-500 hover:text-red-600"
                >
                  <LogOut className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className={`flex-1 transition-all duration-300 ${sidebarOpen ? 'ml-64' : 'ml-20'}`}>
        {/* Top Header */}
        <header className="bg-white border-b border-gray-200 sticky top-0 z-30">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{user.company_name}</h1>
                <p className="text-sm text-gray-600 mt-1">
                  Welcome back, {user.first_name} • {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                </p>
              </div>
              <div className="flex items-center space-x-4">
                <Button
                  onClick={() => navigate('/configurator-select')}
                  className="bg-orange-600 hover:bg-orange-700 text-white"
                >
                  Configurator
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  className="md:hidden"
                >
                  {mobileMenuOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
                </Button>
              </div>
            </div>
          </div>
        </header>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-b border-gray-200 px-4 py-2">
            <div className="flex flex-wrap gap-2">
              {menuItems.map((item) => {
                const Icon = item.icon;
                return (
                  <Button
                    key={item.id}
                    variant={activeTab === item.id ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => {
                      setActiveTab(item.id);
                      setMobileMenuOpen(false);
                    }}
                    className="flex items-center space-x-2"
                  >
                    <Icon className="h-4 w-4" />
                    <span>{item.label}</span>
                  </Button>
                );
              })}
            </div>
          </div>
        )}

        {/* Content Area */}
        <main className="p-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card className="border-l-4 border-l-blue-500 shadow-sm hover:shadow-md transition-shadow">
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium text-gray-600">Total Contacts</CardTitle>
                    <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                      <Users className="h-5 w-5 text-blue-600" />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-gray-900">{contacts.length}</div>
                    <p className="text-xs text-gray-500 mt-1">Customer contacts in database</p>
                  </CardContent>
                </Card>

                <Card className="border-l-4 border-l-green-500 shadow-sm hover:shadow-md transition-shadow">
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium text-gray-600">Team Members</CardTitle>
                    <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                      <UserPlus className="h-5 w-5 text-green-600" />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-gray-900">{teamMembers.length}</div>
                    <p className="text-xs text-gray-500 mt-1">Active team members</p>
                  </CardContent>
                </Card>

                <Card className="border-l-4 border-l-orange-500 shadow-sm hover:shadow-md transition-shadow">
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium text-gray-600">Total Quotes</CardTitle>
                    <div className="h-10 w-10 rounded-full bg-orange-100 flex items-center justify-center">
                      <FileText className="h-5 w-5 text-orange-600" />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-gray-900">{quotes.length}</div>
                    <p className="text-xs text-gray-500 mt-1">All time quotes generated</p>
                  </CardContent>
                </Card>
              </div>

              {/* Recent Activity */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card className="shadow-sm">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-lg font-semibold">Recent Quotes</CardTitle>
                        <CardDescription>Latest quotes created by your team</CardDescription>
                      </div>
                      <Activity className="h-5 w-5 text-gray-400" />
                    </div>
                  </CardHeader>
                  <CardContent>
                    {loading ? (
                      <div className="text-center py-8 text-gray-500">Loading...</div>
                    ) : quotes.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <FileText className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                        <p>No quotes yet</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {quotes.slice(0, 5).map((quote) => (
                          <div
                            key={quote.id}
                            className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 transition-all cursor-pointer"
                          >
                            <div className="flex-1">
                              <div className="flex items-center space-x-2 mb-1">
                                <span className="font-semibold text-gray-900">{quote.quote_number}</span>
                                <Badge className={`${getStatusColor(quote.status)} text-xs`}>
                                  {quote.status}
                                </Badge>
                              </div>
                              <p className="text-xs text-gray-500">
                                {new Date(quote.created_at).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric',
                                })}
                              </p>
                            </div>
                            {quote.total_amount && (
                              <div className="text-right">
                                <div className="font-semibold text-gray-900">
                                  {new Intl.NumberFormat('nl-NL', {
                                    style: 'currency',
                                    currency: quote.currency || 'EUR',
                                  }).format(quote.total_amount)}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Card className="shadow-sm">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-lg font-semibold">Quick Stats</CardTitle>
                        <CardDescription>Overview of your business</CardDescription>
                      </div>
                      <TrendingUp className="h-5 w-5 text-gray-400" />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                            <Users className="h-5 w-5 text-blue-600" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">Active Contacts</p>
                            <p className="text-xs text-gray-500">This month</p>
                          </div>
                        </div>
                        <span className="text-lg font-bold text-gray-900">{contacts.length}</span>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                            <FileText className="h-5 w-5 text-green-600" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">Quotes This Month</p>
                            <p className="text-xs text-gray-500">Generated</p>
                          </div>
                        </div>
                        <span className="text-lg font-bold text-gray-900">
                          {quotes.filter(q => {
                            const quoteDate = new Date(q.created_at);
                            const now = new Date();
                            return quoteDate.getMonth() === now.getMonth() && quoteDate.getFullYear() === now.getFullYear();
                          }).length}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          {/* Contacts Tab */}
          {activeTab === 'contacts' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Contacts</h2>
                  <p className="text-sm text-gray-600 mt-1">Manage your customer contacts</p>
                </div>
                <div className="flex items-center space-x-2">
                  {user && user.role === 'admin' && (
                    <Button 
                      variant="outline"
                      disabled={syncingGHL}
                      onClick={async () => {
                        setSyncingGHL(true);
                        setGhlSyncMessage({ type: 'info', text: 'Syncing contacts from GoHighLevel...' });
                        try {
                          const response = await axios.post(
                            `${API_URL}/api/ghl/sync-contacts`,
                            { overwrite: false },
                            {
                              headers: {
                                'Authorization': `Bearer ${localStorage.getItem('access_token')}`
                              }
                            }
                          );
                          setGhlSyncMessage({ 
                            type: 'success', 
                            text: `Successfully synced ${response.data.synced} contact${response.data.synced !== 1 ? 's' : ''} from GHL${response.data.skipped > 0 ? `. ${response.data.skipped} duplicate${response.data.skipped !== 1 ? 's' : ''} skipped.` : '.'}` 
                          });
                          // Refresh contacts list
                          const contactsRes = await axios.get(`${API_URL}/api/contacts`, {
                            headers: {
                              'Authorization': `Bearer ${localStorage.getItem('access_token')}`
                            }
                          });
                          setContacts(contactsRes.data);
                          // Clear message after 5 seconds
                          setTimeout(() => setGhlSyncMessage(null), 5000);
                        } catch (error) {
                          console.error('Error syncing GHL contacts:', error);
                          setGhlSyncMessage({ 
                            type: 'error', 
                            text: error.response?.data?.detail || 'Failed to sync contacts from GHL. Please check your GHL API credentials.' 
                          });
                          // Clear error message after 7 seconds
                          setTimeout(() => setGhlSyncMessage(null), 7000);
                        } finally {
                          setSyncingGHL(false);
                        }
                      }}
                    >
                      {syncingGHL ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Syncing...
                        </>
                      ) : (
                        <>
                          <ArrowRight className="mr-2 h-4 w-4" />
                          Sync from GHL
                        </>
                      )}
                    </Button>
                  )}
                  <Button className="bg-orange-600 hover:bg-orange-700 shadow-sm">
                    <Plus className="mr-2 h-4 w-4" />
                    Add Contact
                  </Button>
                </div>
              </div>

              <Card className="shadow-sm">
                <CardHeader>
                  <div className="flex items-center space-x-4">
                    <div className="flex-1">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <Input
                          placeholder="Search contacts by name, email, or phone..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="pl-10"
                        />
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <div className="text-center py-12 text-gray-500">Loading contacts...</div>
                  ) : filteredContacts.length === 0 ? (
                    <div className="text-center py-12">
                      <Users className="mx-auto h-16 w-16 text-gray-300 mb-4" />
                      <p className="text-gray-500 font-medium">No contacts found</p>
                      <p className="text-sm text-gray-400 mt-1">Get started by adding your first contact</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="border-b border-gray-200">
                            <TableHead className="font-semibold text-gray-700 w-16 text-sm"></TableHead>
                            <TableHead className="font-semibold text-gray-700 text-sm">Name</TableHead>
                            <TableHead className="font-semibold text-gray-700 text-sm">Phone</TableHead>
                            <TableHead className="font-semibold text-gray-700 text-sm">Email</TableHead>
                            <TableHead className="font-semibold text-gray-700 text-sm">Created</TableHead>
                            <TableHead className="font-semibold text-gray-700 text-sm">Tags</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {filteredContacts.map((contact) => (
                            <TableRow 
                              key={contact.id} 
                              className="hover:bg-gray-50 border-b border-gray-100 cursor-pointer"
                              onClick={() => {
                                navigate(`/dashboard/contact/${contact.id}`);
                              }}
                            >
                              <TableCell className="py-3">
                                <div className="h-9 w-9 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center shadow-sm">
                                  <span className="text-white font-semibold text-xs">
                                    {getInitials(contact.first_name, contact.last_name)}
                                  </span>
                                </div>
                              </TableCell>
                              <TableCell className="font-medium text-gray-900 text-sm py-3">
                                {contact.first_name} {contact.last_name}
                              </TableCell>
                              <TableCell className="text-gray-600 text-sm py-3">
                                {contact.phone ? (
                                  <span>{contact.phone}</span>
                                ) : (
                                  <span className="text-gray-400">-</span>
                                )}
                              </TableCell>
                              <TableCell className="text-gray-600 text-sm py-3">
                                {contact.email ? (
                                  <span className="truncate block max-w-xs">{contact.email}</span>
                                ) : (
                                  <span className="text-gray-400">-</span>
                                )}
                              </TableCell>
                              <TableCell className="text-gray-600 text-xs py-3">
                                {new Date(contact.created_at).toLocaleString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })}
                              </TableCell>
                              <TableCell className="py-3">
                                {contact.notes ? (
                                  <Badge variant="outline" className="text-xs bg-gray-50 border-gray-200">
                                    {contact.notes}
                                  </Badge>
                                ) : (
                                  <span className="text-gray-400 text-sm">-</span>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      
                      {/* Pagination Controls - Only show if more than 50 contacts */}
                      {filteredContacts.length > contactsPerPage && (
                        <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
                          <div className="text-sm text-gray-600">
                            Showing {contactStartIndex + 1} to {Math.min(contactEndIndex, filteredContacts.length)} of {filteredContacts.length} contacts
                          </div>
                          <div className="flex items-center space-x-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setContactCurrentPage(prev => Math.max(1, prev - 1))}
                              disabled={contactCurrentPage === 1}
                            >
                              <ChevronLeft className="h-4 w-4" /> Previous
                            </Button>
                            {Array.from({ length: totalContactPages }, (_, i) => (
                              <Button
                                key={i + 1}
                                variant={contactCurrentPage === i + 1 ? 'default' : 'outline'}
                                size="sm"
                                onClick={() => setContactCurrentPage(i + 1)}
                                className={contactCurrentPage === i + 1 ? 'bg-orange-600 hover:bg-orange-700 text-white' : ''}
                              >
                                {i + 1}
                              </Button>
                            ))}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setContactCurrentPage(prev => Math.min(totalContactPages, prev + 1))}
                              disabled={contactCurrentPage === totalContactPages}
                            >
                              Next <ChevronRight className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Team Tab */}
          {activeTab === 'team' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Team Members</h2>
                  <p className="text-sm text-gray-600 mt-1">Manage your team and permissions</p>
                </div>
                {user && (user.role === 'admin' || user.role === 'Admin') && (
                  <Button 
                    className="bg-orange-600 hover:bg-orange-700 shadow-sm"
                    onClick={() => {
                      console.log('Add Team Member clicked, opening dialog', { userRole: user.role, dialogOpen: teamMemberDialogOpen });
                      setTeamMemberDialogOpen(true);
                    }}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Add Team Member
                  </Button>
                )}
              </div>

              <Card className="shadow-sm">
                <CardContent className="pt-6">
                  {loading ? (
                    <div className="text-center py-12 text-gray-500">Loading team members...</div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {teamMembers.map((member) => (
                        <Card key={member.id} className="hover:shadow-md transition-all border border-gray-200">
                          <CardHeader>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-3">
                                <div className="h-12 w-12 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center">
                                  <span className="text-white font-semibold text-sm">
                                    {member.first_name[0]}{member.last_name[0]}
                                  </span>
                                </div>
                                <div>
                                  <CardTitle className="text-base font-semibold">
                                    {member.first_name} {member.last_name}
                                  </CardTitle>
                                  <Badge variant="outline" className="mt-1 text-xs">
                                    {member.role}
                                  </Badge>
                                </div>
                              </div>
                              {user && (user.role === 'admin' || user.role === 'Admin') && user.id !== member.id && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openDeleteDialog(member)}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  title="Delete team member"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </CardHeader>
                          <CardContent className="space-y-2">
                            <div className="flex items-center text-sm text-gray-600">
                              <Mail className="mr-2 h-4 w-4 text-gray-400" />
                              <span className="truncate">{member.email}</span>
                            </div>
                            {member.phone && (
                              <div className="flex items-center text-sm text-gray-600">
                                <Phone className="mr-2 h-4 w-4 text-gray-400" />
                                {member.phone}
                              </div>
                            )}
                            {member.last_login && (
                              <div className="flex items-center text-sm text-gray-600">
                                <Calendar className="mr-2 h-4 w-4 text-gray-400" />
                                Last login: {new Date(member.last_login).toLocaleDateString()}
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Pricing Settings Tab */}
          {activeTab === 'pricing-settings' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Pricing Settings</h2>
                <p className="text-sm text-gray-600 mt-1">Configure margin settings for quotes. Discount is set per quote when exporting.</p>
              </div>

              <Card className="shadow-sm">
                <CardHeader>
                  <CardTitle>Margin Settings</CardTitle>
                  <CardDescription>
                    Enable margin to add a percentage markup to quote prices. The margin is applied silently and not shown on quotes. Minimum margin is 35%.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      id="margin-enabled"
                      checked={pricingSettings.margin_enabled}
                      onChange={(e) => setPricingSettings({
                        ...pricingSettings,
                        margin_enabled: e.target.checked,
                        margin_percentage: e.target.checked && pricingSettings.margin_percentage < 35 ? 35 : pricingSettings.margin_percentage
                      })}
                      className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                    />
                    <Label htmlFor="margin-enabled" className="text-sm font-medium text-gray-700 cursor-pointer">
                      Enable Margin
                    </Label>
                  </div>
                  
                  {pricingSettings.margin_enabled && (
                    <div className="pl-7 space-y-2">
                      <Label htmlFor="margin-percentage" className="text-sm text-gray-600">
                        Margin Percentage (%) <span className="text-red-500">*Minimum 35%</span>
                      </Label>
                      <Input
                        id="margin-percentage"
                        type="number"
                        min="35"
                        max="100"
                        step="0.01"
                        value={pricingSettings.margin_percentage}
                        onChange={(e) => {
                          const inputValue = e.target.value;
                          // Allow empty input while typing
                          if (inputValue === '') {
                            setPricingSettings({
                              ...pricingSettings,
                              margin_percentage: ''
                            });
                            return;
                          }
                          const value = parseFloat(inputValue);
                          // Allow typing any number, validation happens on blur/save
                          if (!isNaN(value)) {
                            setPricingSettings({
                              ...pricingSettings,
                              margin_percentage: value
                            });
                          }
                        }}
                        onBlur={(e) => {
                          // Validate and correct on blur
                          const value = parseFloat(e.target.value);
                          if (isNaN(value) || value < 35) {
                            setPricingSettings({
                              ...pricingSettings,
                              margin_percentage: 35
                            });
                          }
                        }}
                        className="max-w-xs"
                      />
                      <p className="text-xs text-gray-500">
                        Enter the margin percentage (minimum 35%, e.g., 45 for 45% margin)
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>

              <div className="flex justify-end">
                <Button
                  onClick={handleSavePricingSettings}
                  disabled={savingPricingSettings}
                  className="bg-orange-600 hover:bg-orange-700"
                >
                  {savingPricingSettings ? 'Saving...' : 'Save Settings'}
                </Button>
              </div>
            </div>
          )}

          {/* Quotes Tab */}
          {activeTab === 'quotes' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Quotes</h2>
                  <p className="text-sm text-gray-600 mt-1">View and manage all your quotes</p>
                </div>
                <Button className="bg-orange-600 hover:bg-orange-700 shadow-sm" onClick={() => navigate('/configurator-select')}>
                  <Plus className="mr-2 h-4 w-4" />
                  Create Quote
                </Button>
              </div>

              <Card className="shadow-sm">
                <CardHeader>
                  <div className="flex items-center space-x-4">
                    <div className="flex-1">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <Input
                          placeholder="Search quotes by client name, email, or quote number..."
                          value={quoteSearchTerm}
                          onChange={(e) => setQuoteSearchTerm(e.target.value)}
                          className="pl-10"
                        />
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-6">
                  {loading ? (
                    <div className="text-center py-12 text-gray-500">Loading quotes...</div>
                  ) : filteredQuotes.length === 0 ? (
                    <div className="text-center py-12">
                      <FileText className="mx-auto h-16 w-16 text-gray-300 mb-4" />
                      <p className="text-gray-500 font-medium">
                        {quoteSearchTerm ? 'No quotes found' : 'No quotes yet'}
                      </p>
                      <p className="text-sm text-gray-400 mt-1">
                        {quoteSearchTerm ? 'Try a different search term' : 'Create your first quote to get started'}
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className="space-y-4">
                        {paginatedQuotes.map((quote) => {
                        const clientDetails = quote.quote_data?.clientDetails || {};
                        const clientName = clientDetails.name || '';
                        const clientEmail = clientDetails.email || '';
                        const creatorName = quote.creator_first_name && quote.creator_last_name 
                          ? `${quote.creator_first_name} ${quote.creator_last_name}` 
                          : null;
                        
                        return (
                          <Card key={quote.id} className="hover:shadow-md transition-all border border-gray-200">
                            <CardHeader>
                              <div className="flex items-center justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center space-x-3 mb-2">
                                    <CardTitle className="text-lg font-semibold">{quote.quote_number}</CardTitle>
                                    <Badge className={`${getStatusColor(quote.status)} text-xs font-medium`}>
                                      {quote.status}
                                    </Badge>
                                  </div>
                                  {clientName && (
                                    <div className="mb-1">
                                      <p className="text-sm font-medium text-gray-900">
                                        Client: {clientName}
                                      </p>
                                      {clientEmail && (
                                        <p className="text-xs text-gray-600">
                                          <Mail className="inline mr-1 h-3 w-3" />
                                          {clientEmail}
                                        </p>
                                      )}
                                    </div>
                                  )}
                                  {user && user.role === 'admin' && creatorName && (
                                    <div className="mb-1 mt-2">
                                      <p className="text-xs text-gray-500 font-medium">
                                        Created by: <span className="text-gray-700">{creatorName}</span>
                                        {quote.creator_email && (
                                          <span className="text-gray-500 ml-1">
                                            ({quote.creator_email})
                                          </span>
                                        )}
                                      </p>
                                    </div>
                                  )}
                                  <CardDescription>
                                    Created {new Date(quote.created_at).toLocaleString('en-US', {
                                      month: 'long',
                                      day: 'numeric',
                                      year: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit',
                                    })}
                                  </CardDescription>
                                </div>
                              </div>
                            </CardHeader>
                            <CardContent>
                              <div className="grid grid-cols-2 gap-4 mb-4">
                                {quote.total_amount && (
                                  <div className="p-3 bg-gray-50 rounded-lg">
                                    <p className="text-xs text-gray-600 mb-1">Total Amount</p>
                                    <p className="text-xl font-bold text-gray-900">
                                      {new Intl.NumberFormat('nl-NL', {
                                        style: 'currency',
                                        currency: quote.currency || 'EUR',
                                      }).format(quote.total_amount)}
                                    </p>
                                  </div>
                                )}
                                {quote.valid_until && (
                                  <div className="p-3 bg-gray-50 rounded-lg">
                                    <p className="text-xs text-gray-600 mb-1">Valid Until</p>
                                    <p className="text-lg font-semibold text-gray-900">
                                      {new Date(quote.valid_until).toLocaleDateString('en-US', {
                                        month: 'long',
                                        day: 'numeric',
                                        year: 'numeric',
                                      })}
                                    </p>
                                  </div>
                                )}
                              </div>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => navigate(`/dashboard/quotes/${quote.id}`, { state: { quote } })}
                                className="w-full"
                              >
                                <Eye className="mr-2 h-4 w-4" />
                                View Quote
                              </Button>
                            </CardContent>
                          </Card>
                        );
                      })}
                      </div>
                      
                      {/* Pagination Controls - Only show if more than 5 quotes */}
                      {filteredQuotes.length > quotesPerPage && (
                        <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
                          <div className="text-sm text-gray-600">
                            Showing {startIndex + 1} to {Math.min(endIndex, filteredQuotes.length)} of {filteredQuotes.length} quotes
                          </div>
                          <div className="flex items-center space-x-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setQuoteCurrentPage(prev => Math.max(1, prev - 1))}
                              disabled={quoteCurrentPage === 1}
                              className="flex items-center"
                            >
                              <ChevronLeft className="h-4 w-4 mr-1" />
                              Previous
                            </Button>
                            
                            {/* Page Numbers */}
                            <div className="flex items-center space-x-1">
                              {Array.from({ length: totalQuotePages }, (_, i) => i + 1).map((pageNum) => {
                                // Show first page, last page, current page, and pages around current
                                if (
                                  pageNum === 1 ||
                                  pageNum === totalQuotePages ||
                                  (pageNum >= quoteCurrentPage - 1 && pageNum <= quoteCurrentPage + 1)
                                ) {
                                  return (
                                    <Button
                                      key={pageNum}
                                      variant={pageNum === quoteCurrentPage ? "default" : "outline"}
                                      size="sm"
                                      onClick={() => setQuoteCurrentPage(pageNum)}
                                      className={pageNum === quoteCurrentPage ? "bg-orange-600 hover:bg-orange-700 text-white" : ""}
                                    >
                                      {pageNum}
                                    </Button>
                                  );
                                } else if (
                                  pageNum === quoteCurrentPage - 2 ||
                                  pageNum === quoteCurrentPage + 2
                                ) {
                                  return (
                                    <span key={pageNum} className="px-2 text-gray-400">
                                      ...
                                    </span>
                                  );
                                }
                                return null;
                              })}
                            </div>
                            
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setQuoteCurrentPage(prev => Math.min(totalQuotePages, prev + 1))}
                              disabled={quoteCurrentPage === totalQuotePages}
                              className="flex items-center"
                            >
                              Next
                              <ChevronRight className="h-4 w-4 ml-1" />
                            </Button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </main>
      </div>

      {/* Quote View Dialog */}
      <QuoteView 
        quote={selectedQuote} 
        open={quoteViewOpen} 
        onOpenChange={setQuoteViewOpen}
      />

      {/* Contact Details Dialog */}
      <Dialog open={contactDetailsOpen} onOpenChange={setContactDetailsOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl">Contact Details</DialogTitle>
            <DialogDescription>
              Complete information for {selectedContact?.first_name} {selectedContact?.last_name}
            </DialogDescription>
          </DialogHeader>
          {selectedContact && (
            <div className="space-y-6 mt-4">
              {/* Header with Avatar */}
              <div className="flex items-center space-x-4 pb-4 border-b">
                <div className="h-16 w-16 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center shadow-md">
                  <span className="text-white font-semibold text-lg">
                    {getInitials(selectedContact.first_name, selectedContact.last_name)}
                  </span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {selectedContact.first_name} {selectedContact.last_name}
                  </h3>
                  {selectedContact.notes && (
                    <Badge variant="outline" className="mt-1">
                      {selectedContact.notes}
                    </Badge>
                  )}
                </div>
              </div>

              {/* Contact Information Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Email */}
                {selectedContact.email && (
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Email</label>
                    <div className="flex items-center space-x-2">
                      <Mail className="h-4 w-4 text-gray-400" />
                      <p className="text-sm text-gray-900">{selectedContact.email}</p>
                    </div>
                  </div>
                )}

                {/* Phone */}
                {selectedContact.phone && (
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Phone</label>
                    <div className="flex items-center space-x-2">
                      <Phone className="h-4 w-4 text-gray-400" />
                      <p className="text-sm text-gray-900">{selectedContact.phone}</p>
                    </div>
                  </div>
                )}

                {/* Address */}
                {(selectedContact.address || selectedContact.city || selectedContact.postal_code) && (
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Address</label>
                    <div className="flex items-start space-x-2">
                      <MapPin className="h-4 w-4 text-gray-400 mt-0.5" />
                      <p className="text-sm text-gray-900">
                        {selectedContact.address && selectedContact.address}
                        {selectedContact.city && (selectedContact.address ? `, ${selectedContact.city}` : selectedContact.city)}
                        {selectedContact.postal_code && ` ${selectedContact.postal_code}`}
                        {selectedContact.country && `, ${selectedContact.country}`}
                      </p>
                    </div>
                  </div>
                )}

                {/* Created Date */}
                <div className="space-y-1">
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Created</label>
                  <div className="flex items-center space-x-2">
                    <Calendar className="h-4 w-4 text-gray-400" />
                    <p className="text-sm text-gray-900">
                      {new Date(selectedContact.created_at).toLocaleString('en-US', {
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </p>
                  </div>
                </div>

                {/* Notes/Tags */}
                {selectedContact.notes && (
                  <div className="space-y-1 md:col-span-2">
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Tags / Notes</label>
                    <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded-md border border-gray-200">
                      {selectedContact.notes}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Team Member Dialog */}
      <Dialog open={teamMemberDialogOpen} onOpenChange={setTeamMemberDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Team Member</DialogTitle>
            <DialogDescription>
              Create a new user account. Admin can see all quotes, users can only see their own.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">First Name *</Label>
                <Input
                  id="firstName"
                  value={newTeamMember.first_name}
                  onChange={(e) => setNewTeamMember({...newTeamMember, first_name: e.target.value})}
                  placeholder="John"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">Last Name *</Label>
                <Input
                  id="lastName"
                  value={newTeamMember.last_name}
                  onChange={(e) => setNewTeamMember({...newTeamMember, last_name: e.target.value})}
                  placeholder="Doe"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={newTeamMember.email}
                onChange={(e) => setNewTeamMember({...newTeamMember, email: e.target.value})}
                placeholder="user@example.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password *</Label>
              <Input
                id="password"
                type="password"
                value={newTeamMember.password}
                onChange={(e) => setNewTeamMember({...newTeamMember, password: e.target.value})}
                placeholder="Enter password"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="role">Role *</Label>
              <Select
                value={newTeamMember.role}
                onValueChange={(value) => setNewTeamMember({...newTeamMember, role: value})}
              >
                <SelectTrigger id="role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin (Can see all quotes)</SelectItem>
                  <SelectItem value="user">User (Can only see own quotes)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Phone (Optional)</Label>
              <Input
                id="phone"
                value={newTeamMember.phone}
                onChange={(e) => setNewTeamMember({...newTeamMember, phone: e.target.value})}
                placeholder="+31 6 12345678"
              />
            </div>
            <div className="flex justify-end space-x-2 pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setTeamMemberDialogOpen(false);
                  setNewTeamMember({
                    email: '',
                    password: '',
                    first_name: '',
                    last_name: '',
                    role: 'user',
                    phone: ''
                  });
                }}
              >
                Cancel
              </Button>
              <Button
                className="bg-orange-600 hover:bg-orange-700"
                onClick={async () => {
                  if (!newTeamMember.email || !newTeamMember.password || !newTeamMember.first_name || !newTeamMember.last_name) {
                    alert('Please fill in all required fields');
                    return;
                  }
                  
                  try {
                    setCreatingMember(true);
                    await axios.post(
                      `${API_URL}/api/team`,
                      {
                        ...newTeamMember,
                        company_id: user.company_id
                      },
                      {
                        headers: {
                          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
                        }
                      }
                    );
                    
                    // Refresh team members list
                    const teamRes = await axios.get(`${API_URL}/api/team`, {
                      headers: {
                        'Authorization': `Bearer ${localStorage.getItem('access_token')}`
                      }
                    });
                    setTeamMembers(teamRes.data);
                    
                    // Reset form and close dialog
                    setNewTeamMember({
                      email: '',
                      password: '',
                      first_name: '',
                      last_name: '',
                      role: 'user',
                      phone: ''
                    });
                    setTeamMemberDialogOpen(false);
                    alert('Team member added successfully!');
                  } catch (error) {
                    console.error('Error creating team member:', error);
                    alert(error.response?.data?.detail || 'Failed to create team member. Please try again.');
                  } finally {
                    setCreatingMember(false);
                  }
                }}
                disabled={creatingMember}
              >
                {creatingMember ? 'Creating...' : 'Create Member'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Team Member Confirmation Dialog */}
      <Dialog open={deleteMemberDialogOpen} onOpenChange={setDeleteMemberDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Team Member</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {memberToDelete ? `${memberToDelete.first_name} ${memberToDelete.last_name}` : 'this team member'}? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end space-x-3 mt-6">
            <Button
              variant="outline"
              onClick={() => {
                setDeleteMemberDialogOpen(false);
                setMemberToDelete(null);
              }}
              disabled={deletingMember}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteMember}
              disabled={deletingMember}
            >
              {deletingMember ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;
